import React from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { getBrand } from '../config/brands';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.secondaryColor} 0%, #c3cfe2 100%);
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;

  .logo {
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ContentSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: white;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 1rem;
    margin-top: 2rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;

    li {
      color: #4b5563;
      line-height: 1.8;
      margin-bottom: 0.5rem;
    }
  }
`;

const FooterSection = styled.footer`
  background: #1f2937;
  color: white;
  padding: 2rem;
  text-align: center;
`;

interface CompliancePolicyPageProps {}

const CompliancePolicyPage: React.FC<CompliancePolicyPageProps> = () => {
  const [searchParams] = useSearchParams();
  const { brand: brandParam } = useParams<{ brand?: string }>();
  // Use brand from URL parameter if present, otherwise use ref query parameter
  const refParam = brandParam || searchParams.get('ref');
  const brand = getBrand(refParam || undefined);

  const handleLogoClick = () => {
    const landingPath = refParam ? `/${refParam}` : '/';
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <Header>
          <HeaderContent>
            <div className="logo" onClick={handleLogoClick}>
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }} />
              ) : (
                brand.name
              )}
            </div>
          </HeaderContent>
        </Header>

        <ContentSection>
          <Title>Compliance Policy Overview</Title>
          <Subtitle>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Subtitle>

          <Section>
            <h2>1. Overview</h2>
            <p>
              {brand.name} is committed to maintaining the highest standards of compliance with all applicable laws,
              regulations, and industry best practices. This Compliance Policy outlines our approach to regulatory
              compliance, risk management, and ethical business conduct.
            </p>
          </Section>

          <Section>
            <h2>2. Regulatory Framework</h2>
            <p>
              We operate in accordance with applicable financial services regulations, including but not limited to:
            </p>
            <ul>
              <li>Anti-Money Laundering (AML) regulations</li>
              <li>Know Your Customer (KYC) requirements</li>
              <li>Counter-Terrorist Financing (CTF) regulations</li>
              <li>Data protection and privacy laws (GDPR, CCPA, etc.)</li>
              <li>Payment services regulations</li>
              <li>Consumer protection laws</li>
              <li>Financial crime prevention requirements</li>
            </ul>
          </Section>

          <Section>
            <h2>3. Know Your Customer (KYC) Procedures</h2>
            <h3>3.1 Customer Identification</h3>
            <p>
              We implement robust KYC procedures to verify the identity of all merchants and customers. This includes:
            </p>
            <ul>
              <li>Collection of government-issued identification documents</li>
              <li>Verification of business registration and licensing</li>
              <li>Validation of beneficial ownership information</li>
              <li>Screening against sanctions lists and PEP databases</li>
              <li>Enhanced due diligence for high-risk customers</li>
            </ul>

            <h3>3.2 Ongoing Monitoring</h3>
            <p>
              We continuously monitor customer accounts and transactions to detect suspicious activity and ensure
              ongoing compliance with KYC requirements.
            </p>
          </Section>

          <Section>
            <h2>4. Anti-Money Laundering (AML) Program</h2>
            <p>
              Our comprehensive AML program includes:
            </p>
            <ul>
              <li>Risk-based approach to customer due diligence</li>
              <li>Transaction monitoring and screening systems</li>
              <li>Suspicious Activity Report (SAR) procedures</li>
              <li>Regular AML training for all employees</li>
              <li>Independent audit and review processes</li>
              <li>Designated AML compliance officer</li>
            </ul>
          </Section>

          <Section>
            <h2>5. Sanctions Screening</h2>
            <p>
              We maintain strict compliance with international sanctions programs, including:
            </p>
            <ul>
              <li>Screening against OFAC, UN, EU, and other sanctions lists</li>
              <li>Real-time transaction screening</li>
              <li>Ongoing monitoring of sanctioned entities</li>
              <li>Immediate blocking of prohibited transactions</li>
              <li>Regular updates to sanctions screening databases</li>
            </ul>
          </Section>

          <Section>
            <h2>6. Data Protection and Privacy</h2>
            <p>
              We are committed to protecting the privacy and security of personal data in compliance with applicable
              data protection laws:
            </p>
            <ul>
              <li>Lawful, fair, and transparent data processing</li>
              <li>Data minimization and purpose limitation</li>
              <li>Robust data security measures</li>
              <li>Privacy by design and by default</li>
              <li>Individual rights management (access, correction, deletion)</li>
              <li>Data breach notification procedures</li>
              <li>Cross-border data transfer safeguards</li>
            </ul>
          </Section>

          <Section>
            <h2>7. Fraud Prevention</h2>
            <p>
              We implement comprehensive fraud prevention measures, including:
            </p>
            <ul>
              <li>Advanced fraud detection algorithms</li>
              <li>Multi-factor authentication</li>
              <li>Device fingerprinting and behavioral analysis</li>
              <li>Real-time fraud alerts and notifications</li>
              <li>Collaboration with law enforcement agencies</li>
              <li>Regular fraud risk assessments</li>
            </ul>
          </Section>

          <Section>
            <h2>8. Record Keeping</h2>
            <p>
              We maintain comprehensive records in accordance with regulatory requirements:
            </p>
            <ul>
              <li>Customer identification and verification documents</li>
              <li>Transaction records and supporting documentation</li>
              <li>AML/KYC compliance records</li>
              <li>Audit trails and system logs</li>
              <li>Compliance training records</li>
              <li>Retention periods in line with regulatory requirements</li>
            </ul>
          </Section>

          <Section>
            <h2>9. Reporting Obligations</h2>
            <p>
              We fulfill all regulatory reporting obligations, including:
            </p>
            <ul>
              <li>Suspicious Activity Reports (SARs)</li>
              <li>Currency Transaction Reports (CTRs)</li>
              <li>Large transaction reporting</li>
              <li>Data breach notifications</li>
              <li>Regulatory filings and disclosures</li>
              <li>Cooperation with regulatory inquiries and investigations</li>
            </ul>
          </Section>

          <Section>
            <h2>10. Employee Training and Awareness</h2>
            <p>
              All employees receive comprehensive compliance training, including:
            </p>
            <ul>
              <li>Initial compliance orientation for new hires</li>
              <li>Annual AML/KYC refresher training</li>
              <li>Role-specific compliance training</li>
              <li>Updates on regulatory changes</li>
              <li>Fraud awareness and prevention</li>
              <li>Data protection and privacy training</li>
            </ul>
          </Section>

          <Section>
            <h2>11. Third-Party Risk Management</h2>
            <p>
              We conduct due diligence on all third-party service providers and partners:
            </p>
            <ul>
              <li>Pre-engagement compliance screening</li>
              <li>Contractual compliance obligations</li>
              <li>Ongoing monitoring and review</li>
              <li>Vendor compliance certifications</li>
              <li>Audit rights and oversight</li>
            </ul>
          </Section>

          <Section>
            <h2>12. Whistleblower Protection</h2>
            <p>
              We encourage the reporting of compliance concerns and protect whistleblowers:
            </p>
            <ul>
              <li>Confidential reporting channels</li>
              <li>Non-retaliation policy</li>
              <li>Independent investigation procedures</li>
              <li>Anonymous reporting options</li>
              <li>Protection from adverse employment actions</li>
            </ul>
          </Section>

          <Section>
            <h2>13. Compliance Governance</h2>
            <p>
              Our compliance program is overseen by:
            </p>
            <ul>
              <li>Designated Chief Compliance Officer</li>
              <li>Compliance committee and working groups</li>
              <li>Board of Directors oversight</li>
              <li>Independent compliance audits</li>
              <li>Regular compliance reporting and metrics</li>
            </ul>
          </Section>

          <Section>
            <h2>14. Policy Updates and Review</h2>
            <p>
              This Compliance Policy is reviewed and updated regularly to reflect:
            </p>
            <ul>
              <li>Changes in applicable laws and regulations</li>
              <li>Industry best practices and standards</li>
              <li>Internal audit findings and recommendations</li>
              <li>Emerging risks and threats</li>
              <li>Lessons learned from compliance incidents</li>
            </ul>
          </Section>

          <Section>
            <h2>15. Contact Information</h2>
            <p>
              For compliance-related questions, concerns, or to report potential violations, please contact:
            </p>
            <p>
              <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
                {brand.contactEmail}
              </a>
            </p>
            <p>
              All compliance inquiries will be handled confidentially and investigated promptly in accordance with
              our internal procedures.
            </p>
          </Section>
        </ContentSection>

        <FooterSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
            {brand.footerLogo ? (
              <img src={brand.footerLogo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : brand.logo ? (
              <img src={brand.logo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : null}
          </div>
          <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
          <p>
            Questions? Contact us at{' '}
            <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
              {brand.contactEmail}
            </a>
          </p>
        </FooterSection>
      </PageContainer>
    </ThemeProvider>
  );
};

export default CompliancePolicyPage;
